
import { defineComponent, watch } from "vue";
import JobModal from "@/components/JobModal.vue";
import SpeechSchedule from '@/components/SpeechSchedule.vue'

interface ScheduleViewModel {
  jobId: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime?: any;
  endTime?: any;
  jobDate: string;
  payRate?: number;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  statusInfo?: string;
  clientId?: number;
  clientInfo?: string;
  adjustmentHours?: number;
  bonusHours: number;
  travelExpense: number;
  remarks: string;
  rate: number;
}

interface JobUpdateViewModel {
  jobId?: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime: string;
  endTime: string;
  jobDate: string;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  clientId?: number;
  clientInfo?: string;
  remarks?: string;
  adjustment?: number;
  rate?: number;
  travelExpense?: number;
  payRate?: number;
  bonusHours?: number;
  oldJobId?: number;
}

interface ScheduleViewModelDatewiseList {
  jobDate: string;
  scheduleViewModels: ScheduleViewModel[];
}

const __default__ = defineComponent({
  name: "ScheduleView",
  components: { JobModal , SpeechSchedule },
  props: { baseUrl: String },

  data() {
    return {
      includeAdjustment: false,
      schedule: {} as Record<string, ScheduleViewModelDatewiseList>,
      dayList: [] as Array<{ dateKey: string; date: Date }>,
      hourList: [...Array(24).keys()],
      isAuthorized: false,
      isLoading: false,
      isCreatingJobs: false,
      isLoadingCompanies: false,

      startDate: new Date(),
      endDate: new Date(),
      selectedCompanyId: "",

      companies: [] as Array<{ companyId: number; companyName: string }>,
      
      officers: [] as Array<any>,
      sites: [] as Array<any>,
      jobStatuses: [] as Array<any>,

      // Drag and drop state
      dragSourceJob: null as ScheduleViewModel | null,
      dragSourceDay: null as { dateKey: string; date: Date } | null,
      dragSourceHour: null as number | null,
      isDragging: false,
      isDragOver: false,
      dragOverKey: null as string | null,
      dragTargetDate: null as string | null,
      dragTargetHour: null as number | null,
      dragSourceDate: null as string | null,
      jobsToCreate: 0,
      dateRangeCount: 0,

      // Delete feature properties
      selectMode: false,
      selectedJobIds: new Set<number>(),

      // Toast notification system
      showToast: false,
      toastMessage: "",
      toastType: "success" as "success" | "error" | "info" | "warning",
      toastTimeout: null as number | null,

      // Abort controller for canceling requests
      abortController: null as AbortController | null,
    };
  },

  computed: {
    hasAnyJobs(): boolean {
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels?.length > 0) {
          return true;
        }
      }
      return false;
    },
    
    hasScheduleData(): boolean {
      return Object.keys(this.schedule).length > 0;
    },

    toastIcon(): string {
      const icons: Record<string, string> = {
        success: "✅",
        error: "❌",
        warning: "⚠️",
        info: "ℹ️"
      };
      return icons[this.toastType] || "ℹ️";
    },

    jobSequenceMap(): Record<number, number> {
      const map: Record<number, number> = {};
      let counter = 1;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          const jobs = this.jobsForSlot(day, hour);
          for (const job of jobs) {
            map[job.jobId] = counter++;
          }
        }
      }
      return map;
    },
    
    dailyTotalHours(): Record<string, string> {
      const totals: Record<string, string> = {};
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        let totalMinutes = 0;
        
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
        
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const roundedMinutes = Math.round(minutes * 100) / 100;
        
        totals[day.dateKey] = roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
      }
      return totals;
    },

    grandTotalHours(): string {
      let totalMinutes = 0;
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
      }
      
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const roundedMinutes = Math.round(minutes * 100) / 100;
      
      return roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
    },

    firstNonEmptyHour(): number {
      let earliestHour = 24;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          if (this.jobsForSlot(day, hour).length > 0 && hour < earliestHour) {
            earliestHour = hour;
          }
        }
      }
      return earliestHour === 24 ? 6 : earliestHour;
    },

    filteredHourList(): number[] {
      return this.hourList.filter(hour => {
        if (hour < this.firstNonEmptyHour) return false;
        return this.dayList.some(day => this.jobsForSlot(day, hour).length > 0);
      });
    },

    startDateInput(): string {
      return this.dateToYYYYMMDD(this.startDate);
    },
    endDateInput(): string {
      return this.dateToYYYYMMDD(this.endDate);
    },
  },

  watch: {
    selectedCompanyId() { this.getScheduleForToday(); },
    
    /*
    startDate(newDate) {
      if (newDate) {
        const normalized = new Date(newDate);
        normalized.setHours(0, 0, 0, 0);
        this.startDate = normalized;
        //this.getScheduleForToday();
      }
    },
    endDate(newDate) {
      if (newDate) {
        const normalized = new Date(newDate);
        normalized.setHours(23, 59, 59, 999);
        this.endDate = normalized;
        //this.getScheduleForToday();
      }
    },
    */
    includeAdjustment() { this.getScheduleForToday(); }
  },

  methods: {

    updateStartDate(event: any) {
    if (event.target.value) {
      const date = new Date(event.target.value);
      date.setHours(0, 0, 0, 0);
      this.startDate = date;
      this.getScheduleForToday();
    }
  },
  
  updateEndDate(event: any) {
    if (event.target.value) {
      const date = new Date(event.target.value);
      date.setHours(23, 59, 59, 999);
      this.endDate = date;
      this.getScheduleForToday();
    }
  },

  // Replace the onScheduleLoaded method with this:
 onScheduleLoaded(data: any) {
  //console.log('Schedule loaded from speech:', data);
  
  // Process the schedule data from speech
  if (data && data.schedule) {
    this.schedule = data.schedule;
    this.dayList = data.dates || [];
  }
  
  // Update startDate and endDate from speech
  if (data && data.startDate && data.endDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    
    //console.log('Start date from speech:', start);
    //console.log('End date from speech:', end);
    
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      // Set proper time components
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      
      // Update the reactive properties
      this.startDate = new Date(start);
      this.endDate = new Date(end);
      
      // Force Vue to re-render the date inputs
      this.$forceUpdate();
      
      // Refresh the schedule with the new dates
      this.$nextTick(() => {
        this.getScheduleForToday();
      });
      
      /*console.log('Updated dates in parent:', {
        start: this.dateToYYYYMMDD(this.startDate),
        end: this.dateToYYYYMMDD(this.endDate)
      });
      */
    }
  }
},

    showNotification(message: string, type: "success" | "error" | "info" | "warning" = "info") {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => this.closeToast(), 5000);
    },

    closeToast() {
      this.showToast = false;
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
        this.toastTimeout = null;
      }
    },

    hasJobConflict(day: { dateKey: string; date: Date }, hour: number): boolean {
      if (!this.dragSourceJob || !this.dragSourceDay) return false;
      const existingJobs = this.jobsForSlot(day, hour);
      return existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
    },

    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.selectedJobIds.clear();
    },

    toggleJobSelection(jobId: number) {
      this.selectedJobIds.has(jobId) 
        ? this.selectedJobIds.delete(jobId) 
        : this.selectedJobIds.add(jobId);
    },

    async deleteSelectedJobs() {
      if (this.selectedJobIds.size === 0) {
        this.showNotification("No jobs selected for deletion.", "warning");
        return;
      }
      
      if (!confirm(`Are you sure you want to delete ${this.selectedJobIds.size} job(s)?\n\nThis action cannot be undone.`)) {
        return;
      }
      
      try {
        const jobIdsArray = Array.from(this.selectedJobIds).map(id => ({ Id: id }));
        const response = await fetch(this.baseUrl + "schedule/DeleteJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobIdsArray)
        });
        
        if (response.ok) {
          this.selectedJobIds.clear();
          this.selectMode = false;
          await this.getScheduleForToday();
          this.showNotification(`${jobIdsArray.length} job(s) deleted successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to delete jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error deleting jobs:", error);
        this.showNotification("Failed to delete jobs. Please try again.", "error");
      }
    },

    getTimeTooltip(duty: ScheduleViewModel) {
      const date = duty.jobDate ? new Date(duty.jobDate).toLocaleDateString() : 'Unknown date';
      return `${date} ${this.formatTimeSpan(duty.startTime)}–${this.formatTimeSpan(duty.endTime)}`;
    },

    getAllLookupData() {
      this.isLoadingCompanies = true;
      
      try {
        // Read from localStorage (cached by login)
        const cachedData = localStorage.getItem("lookupData");
        
        if (!cachedData) {
          this.showNotification("Please login again to load data.", "warning");
          this.isAuthorized = false;
          this.isLoadingCompanies = false;
          return;
        }
        
        const data = JSON.parse(cachedData);
        
        this.officers = data.officers || [];
        this.sites = data.sites || [];
        this.jobStatuses = data.jobStatuses || [];
        
        let companiesData = data.companies || [];
        if (companiesData.data && Array.isArray(companiesData.data)) {
          companiesData = companiesData.data;
        }
        if (companiesData.values && Array.isArray(companiesData.values)) {
          companiesData = companiesData.values;
        }
        
        this.companies = (Array.isArray(companiesData) ? companiesData : []).map((company: any) => ({
          companyId: company.companyId || company.id || company.Id || company.companyID || 0,
          companyName: company.companyName || company.name || company.Name || company.companyName || 'Unknown'
        }));
        
        this.isAuthorized = true;
        
      } catch (error) {
        console.error("Error loading lookup data:", error);
        this.showNotification("Failed to load client data.", "error");
      } finally {
        this.isLoadingCompanies = false;
      }
    },

    parseTimeToMinutes(timeValue: any): number {
      if (!timeValue) return 0;
      if (typeof timeValue === 'string') {
        const parts = timeValue.split(':');
        return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1] || '0') || 0);
      }
      if (typeof timeValue === 'object') {
        return (timeValue.Hours || 0) * 60 + (timeValue.Minutes || 0);
      }
      return 0;
    },
    
    formatDayLabel(date: Date) {
      if (!date || isNaN(date.getTime())) return "Invalid";
      return date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short"
      });
    },

    formatHourLabel(hour: number) {
      return `${hour.toString().padStart(2, "0")}:00`;
    },

    formatTimeSpan(timeSpan: any) {
      if (!timeSpan) return "N/A";
      if (typeof timeSpan === 'string') return timeSpan.substring(0, 5);
      if (typeof timeSpan === 'object') {
        const hours = timeSpan.Hours?.toString().padStart(2, '0') || '00';
        const minutes = timeSpan.Minutes?.toString().padStart(2, '0') || '00';
        return `${hours}:${minutes}`;
      }
      return "N/A";
    },

    formatOfficerName(name: string, dayCount: number) {
      if (!name) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      if (name.length <= maxLength) return name;
      
      const parts = name.trim().split(' ');
      if (parts.length === 1) return parts[0].substring(0, maxLength - 2) + '..';
      return `${parts[0]} ${parts[1]?.charAt(0)}.`;
    },

    formatSiteName(site: string, dayCount: number) {
      if (!site) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      const fullName = site.split(' - ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    formatClientName(client: string, dayCount: number) {
      if (!client) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 20 : 10;
      const fullName = client.split(' ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    jobsForSlot(day: { dateKey: string; date: Date }, hour: number) {
      if (!day?.date) return [];
      const dayData = this.schedule[day.dateKey];
      if (!dayData?.scheduleViewModels) return [];
      
      return dayData.scheduleViewModels.filter((duty: ScheduleViewModel) => {
        if (!duty.startTime) return false;
        
        if (duty.jobDate) {
          const jobDateObj = new Date(duty.jobDate);
          const columnDateObj = new Date(day.date);
          if (jobDateObj.toDateString() !== columnDateObj.toDateString()) return false;
        }
        
        let startHour = 0;
        if (typeof duty.startTime === 'string') startHour = parseInt(duty.startTime.substring(0, 2));
        else if (typeof duty.startTime === 'object' && duty.startTime.Hours !== undefined) {
          startHour = duty.startTime.Hours;
        }
        return startHour === hour;
      });
    },

    getDateKey(date: Date): string {
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T00:00:00`;
    },

    setStatusColor(statusId: any) {
      return {
        'status-completed': statusId == 2,
        'status-in-progress': statusId == 4,
        'status-pending': statusId == 3,
        'status-cancelled': statusId == 8
      };
    },

    dateToYYYYMMDD(d: Date) {
      if (!d) return "";
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0];
    },

    calculateJobDuration(startTime: any, endTime: any): string {
      if (!startTime || !endTime) return "0h";
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let durationMinutes = endTotalMinutes - startTotalMinutes;
      if (durationMinutes < 0) durationMinutes = 1440 + durationMinutes;
      
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
    },

    getDurationInMinutes(startTime: any, endTime: any, adjustmentHours = 0): number {
      if (!startTime || !endTime) return 0;
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let duration = endTotalMinutes - startTotalMinutes;
      if (duration < 0) duration = 1440 + duration;
      if (this.includeAdjustment) duration += (adjustmentHours || 0) * 60;
      
      return duration;
    },

    getScheduleForToday() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      
      let companyId = 0;
      if (this.selectedCompanyId) {
        const parsed = parseInt(this.selectedCompanyId);
        if (!isNaN(parsed) && parsed > 0) companyId = parsed;
      }

      fetch(this.baseUrl + "schedule/ScheduleDictionaryForDateRangePageWise", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          StartDate: this.startDate,
          EndDate: this.endDate,
          PageNumber: 0,
          RecordsPerPage: 5000,
          SearchString: "",
          CompanyId: companyId
        }),
        signal: this.abortController.signal
      })
      .then(r => {
        if (r.status === 401) {
          this.isAuthorized = false;
          this.isLoading = false;
          this.showNotification("Session expired. Please login again.", "warning");
          return null;
        }
        this.isAuthorized = true;
        return r.json();
      })
      .then(result => {
        this.isLoading = false;
        if (!result) return;

        const scheduleData = result.scheduleSinglePageResult;
        if (!scheduleData) {
          this.schedule = {};
          this.dayList = [];
          return;
        }

        this.schedule = scheduleData;

        const dayListArray: Array<{ dateKey: string; date: Date }> = [];
        const currentDate = new Date(this.startDate);
        const endDateObj = new Date(this.endDate);
        currentDate.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);

        while (currentDate <= endDateObj) {
          dayListArray.push({
            dateKey: this.getDateKey(currentDate),
            date: new Date(currentDate)
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }

        this.dayList = dayListArray.sort((a, b) => a.date.getTime() - b.date.getTime());
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        this.isLoading = false;
        console.error("Error fetching schedule:", error);
        this.showNotification("Failed to load schedule data.", "error");
      });
    },

    OnAddJobCliked() {
      (this.$refs.jobModal as any).openForAdd();
    },

    openEditJob(duty: ScheduleViewModel) {
      (this.$refs.jobModal as any).openForEdit(duty);
    },

    onDragStart(event: DragEvent, duty: ScheduleViewModel, day: { dateKey: string; date: Date }, hour: number) {
      this.dragSourceJob = duty;
      this.dragSourceDay = day;
      this.dragSourceHour = hour;
      this.isDragging = true;
      this.dragSourceDate = this.formatDayLabel(day.date);
      
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('text/plain', JSON.stringify({
          jobId: duty.jobId,
          dateKey: day.dateKey,
          hour: hour
        }));
      }
      event.stopPropagation();
    },

    onDragEnd() {
      this.dragSourceJob = null;
      this.dragSourceDay = null;
      this.dragSourceHour = null;
      this.isDragging = false;
      this.isDragOver = false;
      this.dragOverKey = null;
      this.dragTargetDate = null;
      this.dragTargetHour = null;
      this.dragSourceDate = null;
      this.dateRangeCount = 0;
      this.jobsToCreate = 0;
    },

    onDragOver(day: { dateKey: string; date: Date }, hour: number) {
      this.isDragOver = true;
      this.dragOverKey = `${day.dateKey}-${hour}`;
      this.dragTargetDate = this.formatDayLabel(day.date);
      this.dragTargetHour = hour;
      
      if (this.dragSourceDay) {
        const startDate = new Date(this.dragSourceDay.date);
        const endDate = new Date(day.date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.dateRangeCount = diffDays + 1;
        this.jobsToCreate = this.dateRangeCount;
      }
    },

    onDragLeave() {
      this.isDragOver = false;
      this.dragOverKey = null;
    },

    async onDrop(day: { dateKey: string; date: Date }, targetHour: number) {
      this.isDragOver = false;
      
      if (!this.dragSourceJob || !this.dragSourceDay) {
        this.onDragEnd();
        return;
      }
      
      const existingJobs = this.jobsForSlot(day, targetHour);
      const hasConflict = existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
      
      if (hasConflict) {
        if (!confirm(`There are already ${existingJobs.length} job(s) at this time. Create additional jobs anyway?`)) {
          this.onDragEnd();
          return;
        }
      }
      
      const isSameDay = this.dragSourceDay.dateKey === day.dateKey;
      const isSameHour = this.dragSourceHour === targetHour;
      
      const startDate = new Date(this.dragSourceDay.date);
      const endDate = new Date(day.date);
      const fromDate = startDate <= endDate ? startDate : endDate;
      const toDate = startDate <= endDate ? endDate : startDate;
      
      const dateRange: Date[] = [];
      const currentDate = new Date(fromDate);
      while (currentDate <= toDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      let datesToCreate: Date[] = [];
      const sourceDateStr = this.dateToYYYYMMDD(this.dragSourceDay.date);
      
      if (isSameDay) {
        datesToCreate = [new Date(day.date)];
      } else {
        datesToCreate = dateRange.filter(date => this.dateToYYYYMMDD(date) !== sourceDateStr);
      }
      
      if (datesToCreate.length === 0) {
        this.showNotification("No new jobs to create.", "info");
        this.onDragEnd();
        return;
      }
      
      const durationMinutes = this.getDurationInMinutes(
        this.dragSourceJob.startTime, 
        this.dragSourceJob.endTime
      );
      
      const newStartTime = this.formatTimeString(targetHour, 0);
      const newEndTime = this.formatTimeString(
        targetHour + Math.floor(durationMinutes / 60), 
        durationMinutes % 60
      );
      
      let currentUserId: number | undefined;
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const userId = tokenPayload.nameid || tokenPayload.userId || tokenPayload.sub;
          if (userId) {
            currentUserId = typeof userId === 'number' ? userId : parseInt(userId);
            if (isNaN(currentUserId as number)) currentUserId = undefined;
          }
        }
      } catch (e) {
        console.warn("Could not decode token:", e);
      }
      
      if (!currentUserId) {
        const userIdFromStorage = localStorage.getItem("userId");
        if (userIdFromStorage) {
          currentUserId = parseInt(userIdFromStorage);
          if (isNaN(currentUserId)) currentUserId = undefined;
        }
      }
      
      let confirmMessage = "";
      if (isSameDay) {
        if (isSameHour) {
          confirmMessage = `Create a DUPLICATE job at the SAME time (${targetHour}:00) on ${this.formatDayLabel(day.date)}?\n\nOriginal job will remain unchanged.`;
        } else {
          confirmMessage = `Create a NEW job at ${targetHour}:00 on the SAME DAY (${this.formatDayLabel(day.date)})?\n\nOriginal job at ${this.dragSourceHour}:00 will remain unchanged.`;
        }
      } else {
        confirmMessage = `Create ${datesToCreate.length} job(s) for "${this.dragSourceJob.securityOfficerInfo}"\nFrom: ${this.formatDayLabel(fromDate)} at ${this.dragSourceHour}:00\nTo: ${this.formatDayLabel(toDate)} at ${targetHour}:00?\n\nThe original job on ${this.formatDayLabel(this.dragSourceDay.date)} will remain unchanged.`;
      }
      
      if (!confirm(confirmMessage)) {
        this.onDragEnd();
        return;
      }
      
      this.isCreatingJobs = true;
      this.jobsToCreate = datesToCreate.length;
      
      try {
        const jobsToCreateList: JobUpdateViewModel[] = [];
        for (const date of datesToCreate) {
          jobsToCreateList.push({
            siteId: this.dragSourceJob.siteId,
            siteInfo: this.dragSourceJob.siteInfo,
            siteDetail: this.dragSourceJob.siteDetail,
            startTime: newStartTime,
            endTime: newEndTime,
            jobDate: this.dateToYYYYMMDD(date),
            securityOfficerId: this.dragSourceJob.securityOfficerId,
            securityOfficerInfo: this.dragSourceJob.securityOfficerInfo,
            operator: currentUserId,
            statusId: this.dragSourceJob.statusId,
            clientId: this.dragSourceJob.clientId,
            clientInfo: this.dragSourceJob.clientInfo,
            remarks: this.dragSourceJob.remarks,
            adjustment: this.dragSourceJob.adjustmentHours,
            rate: this.dragSourceJob.rate,
            travelExpense: this.dragSourceJob.travelExpense,
            bonusHours: this.dragSourceJob.bonusHours,
            payRate: this.dragSourceJob.payRate,
            oldJobId: undefined
          });
        }
        
        const response = await fetch(this.baseUrl + "schedule/AddJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobsToCreateList)
        });
        
        if (response.ok) {
          await this.getScheduleForToday();
          this.showNotification(`${jobsToCreateList.length} job(s) created successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to create jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error creating jobs:", error);
        this.showNotification("Failed to create jobs. Please try again.", "error");
      } finally {
        this.isCreatingJobs = false;
        this.onDragEnd();
      }
    },

    formatTimeString(hours: number, minutes: number): string {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    },
  },

  mounted() {
    this.isAuthorized = !!localStorage.getItem("token");
    this.getAllLookupData();
    this.getScheduleForToday();
  },

  beforeUnmount() {
    if (this.abortController) this.abortController.abort();
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
  }
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "v1dd02538": (_ctx.dayList.length)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__